import React, { useState, useEffect, useCallback } from 'react';
import { reports, suppliers as suppApi } from '../utils/api';
import { fmtBRL, fmtUSD, fmtDate, StatusBadge } from '../utils/helpers';
import { useT } from '../i18n/I18nContext';

const today = new Date().toISOString().split('T')[0];
const firstOfYear = new Date(new Date().getFullYear(),0,1).toISOString().split('T')[0];

export default function Reports() {
  const t = useT();
  const [tab, setTab]         = useState('quotations');
  const [data, setData]       = useState(null);
  const [dataTab, setDataTab] = useState(null); // which tab `data` belongs to
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [suppList, setSuppList] = useState([]);
  const [filters, setFilters] = useState({ from: firstOfYear, to: today, supplier_id:'', collection:'' });

  const TABS = [
    { id:'quotations',  label: t('reports.tab.quotations') },
    { id:'purchases',   label: t('reports.tab.purchases') },
    { id:'suppliers',   label: t('reports.tab.suppliers') },
    { id:'performance', label: t('reports.tab.performance') },
    { id:'funnel',      label: t('reports.tab.funnel') },
    { id:'agents',      label: t('reports.tab.agents') },
  ];

  useEffect(() => {
    suppApi.list({ limit: 200 })
      .then(r => setSuppList(Array.isArray(r) ? r : (r?.rows || [])))
      .catch(() => setSuppList([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setData(null); setError('');
    try {
      let result;
      if (tab==='quotations')  result = await reports.quotations(filters);
      else if (tab==='purchases')   result = await reports.purchases(filters);
      else if (tab==='suppliers')   result = await reports.suppliers();
      else if (tab==='performance') result = await reports.performance(filters);
      else if (tab==='funnel')      result = await reports.funnel();
      else if (tab==='agents')      result = await reports.agents(filters);
      setData(result);
      setDataTab(tab);
    } catch (e) {
      setError(t('reports.load_error'));
      setData(null);
    } finally { setLoading(false); }
  }, [tab, filters, t]);

  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  function sf(k,v) { setFilters(f=>({...f,[k]:v})); }

  return (
    <div className="page">
      <div className="page-header">
        <div><div className="page-title">📊 {t('reports.title')}</div><div className="page-subtitle">{t('reports.subtitle')}</div></div>
        <button className="btn btn-accent" onClick={load} disabled={loading}>{loading?'⏳':'🔄'} {t('reports.update')}</button>
      </div>

      <div style={{display:'flex',gap:4,marginBottom:24,background:'var(--gray-100)',borderRadius:'var(--radius-lg)',padding:4,flexWrap:'wrap'}}>
        {TABS.map(tb=>(
          <button key={tb.id} onClick={()=>setTab(tb.id)} style={{padding:'9px 16px',borderRadius:'var(--radius)',border:'none',cursor:'pointer',fontSize:13,fontWeight:600,background:tab===tb.id?'var(--pantone)':'transparent',color:tab===tb.id?'white':'var(--gray-600)',transition:'all .15s',fontFamily:'var(--font)'}}>
            {tb.label}
          </button>
        ))}
      </div>

      {!['suppliers','funnel'].includes(tab) && (
        <div className="card" style={{marginBottom:20}}>
          <div className="card-body" style={{padding:'14px 20px'}}>
            <div className="gap-8" style={{flexWrap:'wrap'}}>
              <div className="form-group"><label>{t('reports.filter.from')}</label><input type="date" value={filters.from} onChange={e=>sf('from',e.target.value)} /></div>
              <div className="form-group"><label>{t('reports.filter.to')}</label><input type="date" value={filters.to} onChange={e=>sf('to',e.target.value)} /></div>
              <div className="form-group" style={{minWidth:200}}>
                <label>{t('reports.filter.supplier')}</label>
                <select value={filters.supplier_id} onChange={e=>sf('supplier_id',e.target.value)}>
                  <option value="">{t('reports.filter.all_suppliers')}</option>
                  {suppList.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label>{t('reports.filter.collection')}</label><input value={filters.collection} onChange={e=>sf('collection',e.target.value)} placeholder="SS27, AW27..." /></div>
              <div style={{alignSelf:'flex-end'}}><button className="btn btn-primary btn-sm" onClick={load}>{t('reports.filter.apply')}</button></div>
            </div>
          </div>
        </div>
      )}

      {loading && <div style={{textAlign:'center',padding:60,color:'var(--gray-400)'}}>⏳ {t('common.loading')}</div>}

      {!loading && error && (
        <div className="card" style={{padding:'24px',textAlign:'center',color:'var(--red)'}}>
          <div style={{fontSize:28,marginBottom:8}}>⚠️</div>
          <div style={{fontWeight:600,marginBottom:12}}>{error}</div>
          <button className="btn btn-primary btn-sm" onClick={load}>{t('reports.update')}</button>
        </div>
      )}

      {!loading && !error && data && dataTab===tab && tab==='quotations' && <ReportQuotations data={data} />}
      {!loading && !error && data && dataTab===tab && tab==='purchases'  && <ReportPurchases  data={data} />}
      {!loading && !error && data && dataTab===tab && tab==='suppliers'  && <ReportSuppliers  data={data} />}
      {!loading && !error && data && dataTab===tab && tab==='performance'&& <ReportPerformance data={data} />}
      {!loading && !error && data && dataTab===tab && tab==='funnel'     && <ReportFunnel     data={data} />}
      {!loading && !error && data && dataTab===tab && tab==='agents'     && <ReportAgents     data={data} />}
    </div>
  );
}

function KpiStrip({ kpis }) {
  return (
    <div className="kpi-grid" style={{marginBottom:20}}>
      {kpis.map((k,i)=>(
        <div key={i} className="kpi-card">
          <div className="kpi-label">{k.label}</div>
          <div className="kpi-value" style={{fontSize:22,color:k.color||'var(--pantone)'}}>{k.value}</div>
          {k.sub && <div className="kpi-sub">{k.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function ReportQuotations({ data }) {
  const t = useT();
  const { rows = [], totals = {} } = data || {};
  return (
    <>
      <KpiStrip kpis={[
        { label: t('reports.quotations.kpi.total'), value: totals.count },
        { label: t('reports.quotations.kpi.est_brl'), value: fmtBRL(totals.total_brl), color:'var(--green)' },
        { label: t('reports.quotations.kpi.confirmed_brl'), value: fmtBRL(totals.total_confirmed_brl), color:'var(--blue)' },
      ]} />
      <div className="card" style={{padding:0}}>
        <div className="card-header" style={{padding:'16px 20px 0'}}><div className="card-title">{t('reports.quotations.period_title')}</div></div>
        <div className="table-wrap" style={{border:'none'}}>
          <table>
            <thead><tr>
              <th>{t('reports.quotations.col.ref')}</th>
              <th>{t('col.supplier')}</th>
              <th>{t('col.collection')}</th>
              <th>{t('col.items')}</th>
              <th>{t('reports.quotations.col.exchange')}</th>
              <th>{t('reports.quotations.col.est_brl')}</th>
              <th>{t('reports.quotations.col.confirmed_brl')}</th>
              <th>Status</th>
              <th>{t('col.created_at')}</th>
              <th>{t('reports.quotations.col.created_by')}</th>
            </tr></thead>
            <tbody>
              {rows.map(r=>(
                <tr key={r.id}>
                  <td className="mono">{r.reference}</td>
                  <td className="bold">{r.supplier_name}</td>
                  <td><span className="tag">{r.collection||'—'}</span></td>
                  <td>{r.item_count}</td>
                  <td className="mono">R$ {Number(r.exchange_rate).toFixed(2)}</td>
                  <td className="text-green">{fmtBRL(r.total_estimated_brl)}</td>
                  <td style={{color:'var(--blue)',fontWeight:600}}>{r.total_confirmed_brl?fmtBRL(r.total_confirmed_brl):'—'}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td className="text-muted">{fmtDate(r.created_at)}</td>
                  <td className="text-muted">{r.created_by_name||'—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function ReportPurchases({ data }) {
  const t = useT();
  const { rows = [], totals = {}, bySupplier = [], byCollection = [] } = data || {};
  return (
    <>
      <KpiStrip kpis={[
        { label: t('reports.purchases.kpi.total'), value: totals.count },
        { label: t('reports.purchases.kpi.est_brl'), value: fmtBRL(totals.total_brl), color:'var(--green)' },
        { label: t('reports.quotations.kpi.confirmed_brl'), value: fmtBRL(totals.total_confirmed_brl), color:'var(--blue)' },
      ]} />
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>
        <div className="card">
          <div className="card-header"><div className="card-title">{t('reports.purchases.by_supplier')}</div></div>
          <div className="card-body" style={{padding:0}}>
            <table>
              <thead><tr>
                <th>{t('col.supplier')}</th>
                <th>{t('col.category')}</th>
                <th>{t('reports.purchases.col.orders')}</th>
                <th>Total BRL</th>
              </tr></thead>
              <tbody>
                {bySupplier.map((s,i)=>(
                  <tr key={i}>
                    <td className="bold">{s.supplier_name}</td>
                    <td><span className="tag">{s.category||'—'}</span></td>
                    <td>{s.count}</td>
                    <td className="text-green">{fmtBRL(s.total_brl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><div className="card-title">{t('reports.purchases.by_collection')}</div></div>
          <div className="card-body" style={{padding:0}}>
            <table>
              <thead><tr>
                <th>{t('col.collection')}</th>
                <th>{t('reports.purchases.col.orders')}</th>
                <th>Total BRL</th>
              </tr></thead>
              <tbody>
                {byCollection.map((c,i)=>(
                  <tr key={i}>
                    <td><span className="tag">{c.collection}</span></td>
                    <td>{c.count}</td>
                    <td className="text-green">{fmtBRL(c.total_brl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="card" style={{padding:0}}>
        <div className="card-header" style={{padding:'16px 20px 0'}}><div className="card-title">{t('reports.purchases.detail_title')}</div></div>
        <div className="table-wrap" style={{border:'none'}}>
          <table>
            <thead><tr>
              <th>{t('reports.quotations.col.ref')}</th>
              <th>{t('col.supplier')}</th>
              <th>{t('col.category')}</th>
              <th>{t('col.collection')}</th>
              <th>{t('col.items')}</th>
              <th>{t('reports.quotations.col.est_brl')}</th>
              <th>{t('reports.quotations.col.confirmed_brl')}</th>
              <th>{t('col.created_at')}</th>
            </tr></thead>
            <tbody>
              {rows.map(r=>(
                <tr key={r.id}>
                  <td className="mono">{r.reference}</td>
                  <td className="bold">{r.supplier_name}</td>
                  <td><span className="tag">{r.supplier_category||'—'}</span></td>
                  <td><span className="tag">{r.collection||'—'}</span></td>
                  <td>{r.item_count}</td>
                  <td className="text-green">{fmtBRL(r.total_estimated_brl)}</td>
                  <td style={{color:'var(--blue)',fontWeight:600}}>{r.total_confirmed_brl?fmtBRL(r.total_confirmed_brl):'—'}</td>
                  <td className="text-muted">{fmtDate(r.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function ReportSuppliers({ data }) {
  const t = useT();
  const { rows = [], byCategory = [] } = data || {};
  return (
    <>
      <KpiStrip kpis={[
        { label: t('reports.suppliers.kpi.total'), value: rows.length },
        { label: t('reports.suppliers.kpi.high_priority'), value: rows.filter(r=>r.priority==='HIGH').length, color:'var(--red)' },
        { label: t('reports.suppliers.kpi.with_orders'), value: rows.filter(r=>r.total_orders>0).length, color:'var(--green)' },
      ]} />
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>
        <div className="card">
          <div className="card-header"><div className="card-title">{t('reports.suppliers.by_category')}</div></div>
          <div className="card-body" style={{padding:0}}>
            <table>
              <thead><tr>
                <th>{t('col.category')}</th>
                <th>{t('reports.suppliers.col.total')}</th>
                <th>{t('reports.suppliers.col.high')}</th>
                <th>{t('reports.suppliers.col.medium')}</th>
                <th>{t('reports.suppliers.col.low')}</th>
              </tr></thead>
              <tbody>
                {byCategory.map((c,i)=>(
                  <tr key={i}>
                    <td className="bold">{c.category}</td>
                    <td>{c.count}</td>
                    <td style={{color:'var(--red)',fontWeight:700}}>{c.high||0}</td>
                    <td style={{color:'var(--accent)',fontWeight:700}}>{c.medium||0}</td>
                    <td style={{color:'var(--gray-400)'}}>{c.low||0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><div className="card-title">{t('reports.suppliers.top')}</div></div>
          <div className="card-body" style={{padding:0}}>
            <table>
              <thead><tr>
                <th>{t('col.supplier')}</th>
                <th>{t('reports.suppliers.col.quotations')}</th>
                <th>{t('reports.purchases.col.orders')}</th>
                <th>Total BRL</th>
              </tr></thead>
              <tbody>
                {rows.filter(r=>r.total_quotations>0).sort((a,b)=>b.total_brl-a.total_brl).slice(0,8).map((r,i)=>(
                  <tr key={i}>
                    <td className="bold">{r.name}</td>
                    <td>{r.total_quotations}</td>
                    <td style={{color:'var(--green)',fontWeight:700}}>{r.total_orders}</td>
                    <td className="text-green">{fmtBRL(r.total_brl)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function ReportPerformance({ data }) {
  const t = useT();
  const { bySupplier = [] } = data || {};
  return (
    <>
      <KpiStrip kpis={[
        { label: t('reports.performance.kpi.evaluated'), value: bySupplier.length },
        { label: t('reports.performance.kpi.fastest'), value: bySupplier.length?`${bySupplier[0]?.avg_response_days||0}d`:'—', color:'var(--green)' },
        { label: t('reports.performance.kpi.approval_rate'), value: bySupplier.length ? `${Math.round(bySupplier.reduce((s,r)=>s+r.approved,0)/Math.max(bySupplier.reduce((s,r)=>s+r.count,0),1)*100)}%` : '—', color:'var(--blue)' },
      ]} />
      <div className="card" style={{padding:0}}>
        <div className="card-header" style={{padding:'16px 20px 0'}}><div className="card-title">{t('reports.performance.title')}</div></div>
        <div className="table-wrap" style={{border:'none'}}>
          <table>
            <thead><tr>
              <th>{t('col.supplier')}</th>
              <th>{t('reports.performance.col.quotations')}</th>
              <th>{t('reports.performance.col.approved')}</th>
              <th>{t('reports.performance.col.rejected')}</th>
              <th>{t('reports.performance.col.approval_rate')}</th>
              <th>{t('reports.performance.col.avg_response')}</th>
            </tr></thead>
            <tbody>
              {bySupplier.map((s,i)=>(
                <tr key={i}>
                  <td className="bold">{s.supplier_name}</td>
                  <td>{s.count}</td>
                  <td style={{color:'var(--green)',fontWeight:600}}>{s.approved}</td>
                  <td style={{color:'var(--red)',fontWeight:600}}>{s.rejected}</td>
                  <td>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div style={{flex:1,height:6,background:'var(--gray-200)',borderRadius:999}}>
                        <div style={{height:'100%',borderRadius:999,background:'var(--green)',width:`${Math.round(s.approved/Math.max(s.count,1)*100)}%`}} />
                      </div>
                      <span style={{fontSize:12,fontWeight:700,color:'var(--green)',minWidth:36}}>{Math.round(s.approved/Math.max(s.count,1)*100)}%</span>
                    </div>
                  </td>
                  <td style={{fontFamily:'var(--font-mono)',fontWeight:700,color:s.avg_response_days<=5?'var(--green)':s.avg_response_days<=10?'var(--accent)':'var(--red)'}}>
                    {s.avg_response_days>0?`${s.avg_response_days}d`:'—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function ReportFunnel({ data }) {
  const t = useT();
  const list = Array.isArray(data) ? data : [];
  const STATUS_ORDER = ['Draft','Ready to Send','Sent','Response Received','Under Analysis','In Negotiation','Approved','Rejected','Order Placed'];
  const byStatus = {};
  list.forEach(d => { byStatus[d.status] = d; });
  const maxN = Math.max(...list.map(d=>d.n), 1);
  return (
    <>
      <KpiStrip kpis={[
        { label: t('reports.funnel.kpi.total'), value: list.reduce((s,d)=>s+d.n,0) },
        { label: t('reports.funnel.kpi.in_progress'), value: list.filter(d=>!['Approved','Rejected','Order Placed'].includes(d.status)).reduce((s,d)=>s+d.n,0), color:'var(--blue)' },
        { label: t('reports.funnel.kpi.converted'), value: (byStatus['Order Placed']?.n||0), color:'var(--green)' },
      ]} />
      <div className="card">
        <div className="card-header"><div className="card-title">{t('reports.funnel.distribution')}</div></div>
        <div className="card-body">
          {STATUS_ORDER.map(status => {
            const d = byStatus[status];
            if (!d) return null;
            const pct = Math.round(d.n/maxN*100);
            return (
              <div key={status} style={{marginBottom:14}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                  <span style={{fontSize:13,fontWeight:600,color:'var(--navy)'}}>{status}</span>
                  <div className="gap-8">
                    <span style={{fontSize:12,color:'var(--gray-500)'}}>{d.avg_days ? t('reports.funnel.avg_days_label').replace('{n}', Math.round(d.avg_days)) : '—'}</span>
                    <span style={{fontSize:13,fontWeight:700,fontFamily:'var(--font-mono)',color:'var(--pantone)'}}>{d.n}</span>
                  </div>
                </div>
                <div style={{height:10,background:'var(--gray-100)',borderRadius:999}}>
                  <div style={{height:'100%',borderRadius:999,background:status==='Approved'||status==='Order Placed'?'var(--green)':status==='Rejected'?'var(--red)':'var(--pantone)',width:`${pct}%`,transition:'width .4s'}} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function ReportAgents({ data }) {
  const t = useT();
  const rows = Array.isArray(data) ? data : [];
  const totalCotacoes = rows.reduce((s,r) => s + (r.total_cotacoes||0), 0);
  const totalPedidos  = rows.reduce((s,r) => s + (r.total_pedidos||0), 0);
  const totalAprovadas   = rows.reduce((s,r) => s + (r.aprovadas||0), 0);
  const totalCotacoesAll = rows.reduce((s,r) => s + (r.total_cotacoes||0), 0);
  const avgApproval      = totalCotacoesAll > 0 ? Math.round(totalAprovadas / totalCotacoesAll * 100) : 0;

  return (
    <>
      <KpiStrip kpis={[
        { label: t('reports.agents.kpi.active'),            value: rows.length },
        { label: t('reports.agents.kpi.total_quotations'),  value: totalCotacoes },
        { label: t('reports.agents.kpi.total_orders'),      value: totalPedidos, color:'var(--pantone)' },
        { label: t('reports.agents.kpi.avg_approval'),      value: `${avgApproval}%`, color:'var(--green)' },
      ]} />

      <div className="card" style={{padding:0,overflow:'hidden'}}>
        <div className="card-header" style={{padding:'16px 20px 0'}}><div className="card-title">{t('reports.agents.performance_title')}</div></div>
        <div className="table-wrap" style={{border:'none'}}>
          <table>
            <thead>
              <tr>
                <th>{t('col.name')}</th>
                <th style={{textAlign:'right'}}>{t('reports.agents.col.quotations')}</th>
                <th style={{textAlign:'right'}}>{t('reports.agents.col.orders')}</th>
                <th style={{textAlign:'right'}}>{t('reports.agents.col.approved')}</th>
                <th style={{textAlign:'right'}}>{t('reports.agents.col.rejected')}</th>
                <th style={{textAlign:'right'}}>{t('reports.agents.col.approval_rate')}</th>
                <th style={{textAlign:'right'}}>{t('reports.agents.col.avg_response')}</th>
                <th style={{textAlign:'right'}}>{t('reports.agents.col.volume_brl')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={8} style={{textAlign:'center',color:'var(--gray-400)',padding:32}}>{t('reports.agents.empty')}</td></tr>
              )}
              {rows.map(r => {
                const taxa = r.total_cotacoes > 0 ? Math.round((r.aprovadas||0)/r.total_cotacoes*100) : 0;
                return (
                  <tr key={r.id}>
                    <td style={{fontWeight:600}}>{r.name}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--font-mono)'}}>{r.total_cotacoes||0}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--font-mono)',fontWeight:600,color:'var(--pantone)'}}>{r.total_pedidos||0}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--font-mono)',color:'var(--green)'}}>{r.aprovadas||0}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--font-mono)',color:'var(--red)'}}>{r.rejeitadas||0}</td>
                    <td style={{textAlign:'right',fontFamily:'var(--font-mono)',fontWeight:600}}>{taxa}%</td>
                    <td style={{textAlign:'right',fontFamily:'var(--font-mono)',color:'var(--gray-500)'}}>
                      {r.avg_response_days ? `${Math.round(r.avg_response_days)}d` : '—'}
                    </td>
                    <td style={{textAlign:'right',fontFamily:'var(--font-mono)'}}>
                      {r.total_brl ? fmtBRL(r.total_brl) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
